class MainProductSection extends HTMLElement {
  connectedCallback() {
    this._qty = 1;
    this._variantId = parseInt(this.dataset.variantId, 10);
    this._basePriceCents = parseInt(this.dataset.basePriceCents, 10) || 0;
    this._baseCompareCents = parseInt(this.dataset.baseCompareCents, 10) || 0;
    this._currentDiscountPct = 0;

    this._initScrollBar();
    this._initQtyOptions();
    this._initOptionSelects();
    this._initSiblingDropdown();
    this._initFullOptionCards();
    this._initReviewSlider();
    this._initRoutineAtcAll();
    this._initRoutineSelectedState();
    this._initDeliveryChecker();
    this._initBuyBtn();
    this._initStickyBar();
    this._initStickyTicker();
    this._stripEmojiBullets();
    this._initScrollToReviews();

    // Start-option cards (rcf-start-options.js) switch the product being bought.
    this.addEventListener('theme:pdp-option:changed', (e) => this._applyOptionVariant(e.detail));

    this._captureDefaultImages();

    // Sync displayed price with whatever qty button is active on load
    // (handles the case where SSR doesn't apply the badge discount).
    this._recalcMainPrice();

    this._initBestPrice();
  }

  // Capture SSR-rendered images so we can restore them when switching back
  // to a variant that has no Shopify-assigned featured_image.
  _captureDefaultImages() {
    const mainImg = this.querySelector('[data-mps-main-img]');
    if (mainImg) {
      this._defaultMainImgSrc = mainImg.src;
      this._defaultMainImgSrcset = mainImg.srcset;
      this._defaultMainImgAlt = mainImg.alt;
    }
    const mobileImg = this.querySelector('[data-mps-main-img-mobile]');
    if (mobileImg) {
      this._defaultMobileImgSrc = mobileImg.src;
      this._defaultMobileImgSrcset = mobileImg.srcset;
      this._defaultMobileImgAlt = mobileImg.alt;
    }
  }

  /* ---- Best price (variant-level metafield-driven container) ---- */
  _initBestPrice() {
    this._bestPriceEl = this.querySelector('[data-best-price]');
    const mapEl = this.querySelector('[data-best-price-map]');
    if (!this._bestPriceEl || !mapEl) return;
    try {
      this._bestPriceMap = JSON.parse(mapEl.textContent);
    } catch (e) {
      this._bestPriceMap = null;
    }
  }

  // Swap the best-price container's content for the given variant. Money is
  // pre-formatted server-side (see product-best-price.liquid) — never format
  // prices in JS. Each element hides when its own source is empty; the whole
  // container hides when the variant has no best price.
  _updateBestPrice(variantId) {
    const container = this._bestPriceEl;
    if (!container || !this._bestPriceMap) return;

    const data = this._bestPriceMap[variantId];
    const price = data && data.price ? data.price : '';
    if (!price) {
      container.classList.add('best-price--hidden');
      return;
    }
    container.classList.remove('best-price--hidden');

    const valueEl = container.querySelector('[data-bp-value]');
    if (valueEl) valueEl.textContent = price;

    const note = data.note || '';
    const noteEl = container.querySelector('[data-bp-note]');
    const dividerEl = container.querySelector('[data-bp-divider]');
    if (noteEl) {
      noteEl.textContent = note;
      noteEl.toggleAttribute('hidden', !note);
    }
    if (dividerEl) dividerEl.toggleAttribute('hidden', !note);

    const info = data.info || '';
    const infoEl = container.querySelector('[data-bp-info]');
    const tooltipEl = container.querySelector('[data-bp-tooltip]');
    if (tooltipEl) tooltipEl.textContent = info;
    if (infoEl) infoEl.toggleAttribute('hidden', !info);
  }

  /* ---- Scroll to Judge.me reviews on rating click ---- */
  _initScrollToReviews() {
    const ratingEl = this.querySelector('[data-scroll-to-reviews]');
    if (!ratingEl) return;
    ratingEl.addEventListener('click', (e) => {
      const target = document.getElementById('judgeme_product_reviews');
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ---- Routine kit: highlight selected items ---- */
  _initRoutineSelectedState() {
    const checkboxes = this.querySelectorAll('[data-routine-check]');
    const sync = (cb) => {
      const item = cb.closest('[data-routine-item]');
      if (!item) return;
      item.classList.toggle('section-mps__routine-item--selected', cb.checked);
    };
    checkboxes.forEach(cb => {
      sync(cb);
      cb.addEventListener('change', () => sync(cb));
    });
  }

  /* ---- Strip leading emojis/symbols from small-card bullets ---- */
  _stripEmojiBullets() {
    const lists = this.querySelectorAll('[data-strip-emoji]');
    lists.forEach(list => {
      list.querySelectorAll('li').forEach(li => {
        const original = li.textContent || '';
        const cleaned = original.replace(/^[^\p{L}\p{N}]+/u, '').trim();
        if (cleaned !== original) li.textContent = cleaned;
      });
    });
  }

  /* ---- Mobile image scroll bar ---- */
  _initScrollBar() {
    const slider = this.querySelector('[data-mobile-slider]');
    const bar = this.querySelector('[data-scroll-bar]');
    if (!slider || !bar) return;

    const update = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      if (maxScroll <= 0) return;
      const pct = slider.scrollLeft / maxScroll;
      const trackWidth = bar.parentElement.clientWidth;
      bar.style.transform = `translateX(${pct * (trackWidth - 15)}px)`;
    };

    slider.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---- Quantity buttons: pick qty + parse the badge percentage ---- */
  _initQtyOptions() {
    const btns = this.querySelectorAll('[data-qty-btn]');
    if (btns.length === 0) return;

    // Seed initial state from whichever button is rendered active
    const seed = Array.from(btns).find(b => b.classList.contains('section-mps__qty-btn--active')) || btns[0];
    this._qty = parseInt(seed.dataset.qty, 10) || 1;
    this._currentDiscountPct = this._readQtyDiscount(seed);
    this._setQuantityInput(this._qty);

    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => {
          b.classList.remove('section-mps__qty-btn--active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('section-mps__qty-btn--active');
        btn.setAttribute('aria-checked', 'true');
        this._qty = parseInt(btn.dataset.qty, 10) || 1;
        this._currentDiscountPct = this._readQtyDiscount(btn);
        this._setQuantityInput(this._qty);
        this._recalcMainPrice();
      });
    });
  }

  _readQtyDiscount(btn) {
    const label = btn.querySelector('.section-mps__qty-discount');
    if (!label) return 0;
    const match = (label.textContent || '').match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? parseFloat(match[1]) : 0;
  }

  _setQuantityInput(qty) {
    const input = this.querySelector('[data-quantity-input]');
    if (input) input.value = qty;
  }

  /* ---- Recalc main price block + main ATC (full ATC stays untouched) ---- */
  _recalcMainPrice() {
    const pct = this._currentDiscountPct || 0;
    const unitSale = Math.round(this._basePriceCents * (1 - pct / 100));
    const unitCompare = this._baseCompareCents || 0;

    const saleEl = this.querySelector('[data-sale-price]');
    if (saleEl) saleEl.textContent = this._formatMoney(unitSale);

    const compareEl = this.querySelector('[data-compare-price]');
    if (compareEl && unitCompare) compareEl.textContent = this._formatMoney(unitCompare);

    /* Discount badge. Recomputed from the two amounts actually on screen so it
       stays truthful when a quantity discount moves the sale price. */
    const pctEl = this.querySelector('[data-price-pct]');
    if (pctEl) {
      const off = unitCompare > unitSale ? Math.round(((unitCompare - unitSale) / unitCompare) * 100) : 0;
      if (off > 0) {
        pctEl.textContent = `${off}% Off`;
        pctEl.removeAttribute('hidden');
      } else {
        pctEl.setAttribute('hidden', '');
      }
    }

    const atcPriceEl = this.querySelector('[data-atc-price]');
    if (atcPriceEl) atcPriceEl.textContent = this._formatMoney(unitSale);

    /* Mirror qty + unit price into the sticky bar — qty controls the cart
       form input, price stays per-unit regardless of qty selected. */
    this._syncStickyBar(this._qty || 1, unitSale);
  }

  _syncStickyBar(qty, totalSale) {
    const sticky = this.querySelector('[data-mps-sticky-bar]');
    if (!sticky) return;

    const qtyValue = sticky.querySelector('[data-sticky-qty-value]');
    if (qtyValue) qtyValue.textContent = qty;

    const stickyQtyInput = sticky.querySelector('[data-sticky-quantity-input]');
    if (stickyQtyInput) stickyQtyInput.value = qty;

    const stickyVariantInput = sticky.querySelector('[data-sticky-variant-input]');
    if (stickyVariantInput && this._variantId) stickyVariantInput.value = this._variantId;

    const priceEl = sticky.querySelector('[data-sticky-atc-price]');
    if (priceEl) priceEl.textContent = this._formatMoney(totalSale);

    /* Disable +/- buttons at boundaries (min 1, max 10). */
    const MAX_QTY = 10;
    const qtyBtns = Array.from(this.querySelectorAll('[data-qty-btn]'));
    const dec = sticky.querySelector('[data-sticky-qty-dec]');
    const inc = sticky.querySelector('[data-sticky-qty-inc]');
    if (qtyBtns.length > 0) {
      const activeIdx = qtyBtns.findIndex((b) => b.classList.contains('section-mps__qty-btn--active'));
      const firstBlockQty = parseInt(qtyBtns[0].dataset.qty, 10) || 1;
      if (dec) dec.disabled = activeIdx <= 0 && qty <= firstBlockQty;
      if (inc) inc.disabled = qty >= MAX_QTY;
    } else {
      if (dec) dec.disabled = qty <= 1;
      if (inc) inc.disabled = qty >= MAX_QTY;
    }
  }

  /* ---- Sticky mobile bar ---- */
  _initStickyBar() {
    const sticky = this.querySelector('[data-mps-sticky-bar]');
    if (!sticky) return;

    /* Watch the main ATC and a few full-width sections (option cards, routine
       kit): hide the sticky bar while any of them is in view, reveal it only
       once they have ALL scrolled off-screen. Falls back to "always visible"
       if the browser doesn't support IntersectionObserver. */
    const mainAtc = this.querySelector('[data-atc-btn]');
    const ocWrapper = document.querySelector('.section-pdp-oc-wrapper');
    const routineKit = document.querySelector('.section-product-routine-kit');
    const watched = [mainAtc, ocWrapper, routineKit].filter(Boolean);

    if (watched.length && 'IntersectionObserver' in window) {
      /* Track each watched element's in-view state; the bar is hidden as long
         as any of them is intersecting. The CSS transition keeps it smooth. */
      const inView = new WeakSet();
      const updateVisibility = () => {
        const anyInView = watched.some((el) => inView.has(el));
        sticky.classList.toggle('is-visible', !anyInView);
        sticky.setAttribute('aria-hidden', anyInView ? 'true' : 'false');
      };

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              inView.add(entry.target);
            } else {
              inView.delete(entry.target);
            }
          });
          updateVisibility();
        },
        { rootMargin: '0px', threshold: 0 }
      );
      watched.forEach((el) => io.observe(el));
    } else if (watched.length) {
      sticky.classList.add('is-visible');
      sticky.setAttribute('aria-hidden', 'false');
    }

    /* +/- buttons step through the existing qty-option blocks (qty=1,
       qty=2 with discount, etc.) so tier discounts apply automatically. */
    const dec = sticky.querySelector('[data-sticky-qty-dec]');
    const inc = sticky.querySelector('[data-sticky-qty-inc]');

    const MAX_QTY = 10;
    const shift = (delta) => {
      const qtyBtns = Array.from(this.querySelectorAll('[data-qty-btn]'));
      if (qtyBtns.length === 0) {
        // No qty option blocks — nudge plain qty (min 1, max MAX_QTY).
        this._qty = Math.max(1, Math.min(MAX_QTY, (this._qty || 1) + delta));
        this._setQuantityInput(this._qty);
        this._recalcMainPrice();
        return;
      }
      /* When qty-option blocks exist, first try stepping through them.
         Once past the last block, continue incrementing plain qty up to MAX_QTY. */
      const activeIdx = qtyBtns.findIndex((b) => b.classList.contains('section-mps__qty-btn--active'));
      const lastBlockQty = parseInt(qtyBtns[qtyBtns.length - 1].dataset.qty, 10) || 1;
      const currentQty = this._qty || 1;

      if (delta > 0 && activeIdx === qtyBtns.length - 1 && currentQty >= lastBlockQty) {
        // Already at last block — increment plain qty up to MAX_QTY
        const nextQty = Math.min(MAX_QTY, currentQty + 1);
        if (nextQty !== currentQty) {
          this._qty = nextQty;
          this._currentDiscountPct = this._readQtyDiscount(qtyBtns[qtyBtns.length - 1]);
          this._setQuantityInput(this._qty);
          this._recalcMainPrice();
        }
      } else if (delta < 0 && activeIdx === qtyBtns.length - 1 && currentQty > lastBlockQty) {
        // Beyond last block — decrement plain qty back toward last block qty
        this._qty = Math.max(lastBlockQty, currentQty - 1);
        this._setQuantityInput(this._qty);
        this._recalcMainPrice();
      } else {
        const nextIdx = Math.max(0, Math.min(qtyBtns.length - 1, activeIdx + delta));
        if (nextIdx !== activeIdx) qtyBtns[nextIdx].click();
      }
    };

    if (dec) dec.addEventListener('click', () => shift(-1));
    if (inc) inc.addEventListener('click', () => shift(1));

    /* Sync once on init so qty + price + boundary disabled state are
       correct when the bar first appears. */
    this._recalcMainPrice();
  }

  /* ---- Sticky bar ticker (vertical slide-up) ---- */
  _initStickyTicker() {
    const ticker = this.querySelector('[data-sticky-ticker]');
    if (!ticker) return;

    const items = ticker.querySelectorAll('.section-mps__sticky-bar-ticker-item');
    if (items.length <= 1) return;

    let current = 0;
    const INTERVAL = 3000;
    const TRANSITION = 400;

    setInterval(() => {
      const prev = items[current];
      current = (current + 1) % items.length;
      const next = items[current];

      prev.classList.add('is-exiting');
      prev.classList.remove('is-active');

      next.classList.add('is-active');

      setTimeout(() => {
        prev.classList.remove('is-exiting');
      }, TRANSITION);
    }, INTERVAL);
  }

  /* ---- Option pickers: custom dropdown (UL/LI) and/or button groups ----
     Each picker owns ONE product option and reports the value the shopper
     chose. The combination of all pickers is resolved against the variant
     matrix emitted by the section, so this works for single-option products
     (Size only) and multi-option ones (Size x Pack Size) without branching. */
  _initSiblingDropdown() {
    this._variants = this._readVariantData();

    const current = this._variants.find(v => v.id === this._variantId);
    // Seed from the SSR-selected variant so a picker change only ever mutates
    // one position and leaves the others as rendered.
    this._selectedOptions = current ? current.options.slice() : [];

    this.querySelectorAll('[data-mps-sibling-btn]').forEach(btn => {
      btn.addEventListener('click', () => this._selectOptionValue(btn));
    });

    const dropdowns = this.querySelectorAll('[data-mps-sibling-dropdown]');

    dropdowns.forEach((dropdown) => {
      const toggle = dropdown.querySelector('[data-mps-sibling-toggle]');
      const menu = dropdown.querySelector('[data-mps-sibling-menu]');
      if (!toggle || !menu) return;

      const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';
      const open = () => {
        menu.removeAttribute('hidden');
        toggle.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        menu.setAttribute('hidden', '');
        toggle.setAttribute('aria-expanded', 'false');
      };

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isOpen()) close();
        else open();
      });

      toggle.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (isOpen()) close();
          else open();
        } else if (e.key === 'Escape' && isOpen()) {
          close();
        }
      });

      menu.addEventListener('click', (e) => {
        const option = e.target.closest('[data-option-value]');
        if (!option) return;
        this._selectOptionValue(option);
        close();
      });

      // Close when clicking outside the dropdown.
      document.addEventListener('click', (e) => {
        if (isOpen() && !dropdown.contains(e.target)) close();
      });

      // Close on Escape from anywhere.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isOpen()) close();
      });
    });

    this._refreshOptionAvailability();
  }

  _readVariantData() {
    const el = this.querySelector('[data-mps-variant-data]');
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  /* Find the variant matching every currently selected option value. */
  _matchVariant(options) {
    return this._variants.find(v =>
      Array.isArray(v.options) && v.options.length === options.length
        && v.options.every((opt, i) => opt === options[i])
    );
  }

  /* A shopper clicked a value in one picker. Swap that single option
     position, then resolve and apply the resulting variant. */
  _selectOptionValue(el) {
    const group = el.closest('[data-mps-option-group]');
    if (!group) return;

    const position = parseInt(group.dataset.optionPosition, 10);
    const value = el.dataset.optionValue;
    if (!position || value == null) return;

    this._selectedOptions[position - 1] = value;

    const isBtn = el.tagName === 'BUTTON';
    group.querySelectorAll('[data-option-value]').forEach(opt => {
      const on = opt === el;
      opt.classList.toggle(isBtn ? 'section-mps__sibling-btn--active' : 'is-active', on);
      opt.setAttribute(isBtn ? 'aria-checked' : 'aria-selected', on ? 'true' : 'false');
    });

    const label = group.querySelector('[data-mps-sibling-label]');
    if (label) label.textContent = value;

    const variant = this._matchVariant(this._selectedOptions);
    if (variant) this._applyVariantData(variant);

    this._refreshOptionAvailability();
  }

  /* Grey out values that cannot be reached from the current selection —
     either the combination does not exist or it is sold out. */
  _refreshOptionAvailability() {
    if (this._variants.length === 0) return;

    this.querySelectorAll('[data-mps-option-group]').forEach(group => {
      const position = parseInt(group.dataset.optionPosition, 10);
      if (!position) return;

      group.querySelectorAll('[data-option-value]').forEach(opt => {
        const probe = this._selectedOptions.slice();
        probe[position - 1] = opt.dataset.optionValue;
        const match = this._matchVariant(probe);
        const usable = Boolean(match && match.available);

        opt.classList.toggle('is-unavailable', !usable);
        if (usable) opt.removeAttribute('aria-disabled');
        else opt.setAttribute('aria-disabled', 'true');
      });
    });
  }

  /* Push a resolved variant into price, image, form inputs and URL. */
  _applyVariantData(variant) {
    // Only overwrite stored price if the variant actually carries one; guard
    // against a missing / zero price keeping the previous price shown.
    if (variant.price > 0) this._basePriceCents = variant.price;
    this._baseCompareCents = variant.compare || 0;

    // Set before _recalcMainPrice so the sticky bar mirrors this variant, not the previous one.
    this._variantId = variant.id;

    this._syncCompareWrap(variant);
    this._recalcMainPrice();

    const variantInput = this.querySelector('[data-variant-input]');
    if (variantInput) variantInput.value = variant.id;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
    this._updateBestPrice(variant.id);
    this._swapHeroImage(variant);
  }

  // Update desktop hero image. Fall back to the SSR default when the variant
  // has no Shopify-assigned featured_image.
  _swapHeroImage(variant) {
    const mainImg = this.querySelector('[data-mps-main-img]');
    if (mainImg) {
      if (variant.img) {
        mainImg.src = variant.img;
        mainImg.srcset = variant.imgSrcset || '';
        mainImg.alt = variant.imgAlt || mainImg.alt;
      } else if (this._defaultMainImgSrc) {
        mainImg.src = this._defaultMainImgSrc;
        mainImg.srcset = this._defaultMainImgSrcset || '';
        mainImg.alt = this._defaultMainImgAlt || '';
      }
    }

    // Update first mobile slide image.
    const mobileImg = this.querySelector('[data-mps-main-img-mobile]');
    if (mobileImg) {
      if (variant.imgMobile) {
        mobileImg.src = variant.imgMobile;
        mobileImg.srcset = variant.imgMobileSrcset || '';
        mobileImg.alt = variant.imgAlt || mobileImg.alt;
      } else if (this._defaultMobileImgSrc) {
        mobileImg.src = this._defaultMobileImgSrc;
        mobileImg.srcset = this._defaultMobileImgSrcset || '';
        mobileImg.alt = this._defaultMobileImgAlt || '';
      }
    }

    // Lightbox hero is only marked in rcf-new-main-section.
    const lightboxImg = this.querySelector('[data-mps-lightbox-hero]');
    if (lightboxImg && variant.imgLarge) {
      lightboxImg.src = variant.imgLarge;
      lightboxImg.srcset = variant.imgLargeSrcset || '';
      lightboxImg.alt = variant.imgAlt || lightboxImg.alt;
    }
  }

  /* A start-option card picked another product/variant. Price block, ATC price
     and sticky bar follow it; gallery and URL stay on the page product. */
  _applyOptionVariant(variant) {
    if (!variant || !variant.id) return;
    this._variantId = variant.id;
    this._basePriceCents = variant.price || 0;
    this._baseCompareCents = variant.compare || 0;
    this._syncCompareWrap(variant);
    this._recalcMainPrice();
    this._updateBestPrice(variant.id);

    if (this.dataset.startMedia === 'selected_product' && variant.productChanged && variant.productUrl) {
      this._loadOptionGallery(variant);
    } else {
      this._swapHeroImage(variant);
    }
  }

  /* 'Show the selected product's media' mode: fetch the selected product's own
     admin media from sections/rcf-product-gallery.liquid and swap in its gallery + lightbox. */
  async _loadOptionGallery(variant) {
    const token = Symbol('gallery');
    this._galleryToken = token;

    const url = new URL(variant.productUrl, window.location.origin);
    url.searchParams.set('section_id', 'rcf-product-gallery');
    url.searchParams.set('variant', variant.id);

    this._galleryCache = this._galleryCache || new Map();
    try {
      if (!this._galleryCache.has(url.href)) {
        const response = await fetch(url.href);
        if (!response.ok) throw new Error(`Gallery fetch failed: ${response.status}`);
        this._galleryCache.set(url.href, await response.text());
      }
      if (this._galleryToken !== token) return;

      const doc = new DOMParser().parseFromString(this._galleryCache.get(url.href), 'text/html');
      const freshImages = doc.querySelector('.section-mps__images');
      const images = this.querySelector('.section-mps__images');
      if (!freshImages || !images) throw new Error('Gallery markup missing');
      images.replaceWith(freshImages);

      const freshLightbox = doc.querySelector('mps-image-lightbox');
      const lightbox = this.querySelector('mps-image-lightbox');
      if (lightbox && freshLightbox) lightbox.replaceWith(freshLightbox);
      else if (lightbox) lightbox.remove();
      else if (freshLightbox) this.append(freshLightbox);

      this._initScrollBar();
      this._captureDefaultImages();
    } catch (error) {
      this._galleryCache.delete(url.href);
      if (this._galleryToken === token) this._swapHeroImage(variant);
    }
  }

  _syncCompareWrap(variant) {
    const priceBlock = this.querySelector('[data-price-block]');
    const compareWrap = priceBlock && priceBlock.querySelector('[data-compare-wrap]');
    if (compareWrap) {
      const showCompare = priceBlock.dataset.showCompare !== 'false';
      if (showCompare && variant.compare > variant.price) {
        compareWrap.removeAttribute('hidden');
      } else {
        compareWrap.setAttribute('hidden', '');
      }

      // Variant-level custom.mrp_toggle: true hides the "MRP " prefix.
      const mrpLabel = compareWrap.querySelector('[data-mrp-label]');
      if (mrpLabel) {
        if (variant.mrp === true) mrpLabel.setAttribute('hidden', '');
        else mrpLabel.removeAttribute('hidden');
      }
    }
  }

  /* ---- Variant dropdowns ---- */
  _initOptionSelects() {
    const selects = this.querySelectorAll('[data-option-select]');
    selects.forEach(select => {
      select.addEventListener('change', () => this._resolveVariant());
    });
  }

  _resolveVariant() {
    const selectedOptions = Array.from(
      this.querySelectorAll('[data-option-select]')
    ).map(s => s.value);

    const meta = window.ShopifyAnalytics?.meta?.product;
    if (!meta) return;

    const match = (meta.variants || []).find(v =>
      Array.isArray(v.options)
        ? v.options.every((opt, i) => opt === selectedOptions[i])
        : v.option1 === selectedOptions[0]
    );

    if (match) this._applyVariant(match.id, match.price, match.compare_at_price);
  }

  _applyVariant(variantId, priceCents, compareCents) {
    this._variantId = variantId;
    this._basePriceCents = priceCents || this._basePriceCents;
    if (compareCents != null) this._baseCompareCents = compareCents || 0;

    const variantInput = this.querySelector('[data-variant-input]');
    if (variantInput) variantInput.value = variantId;

    this._recalcMainPrice();
    this._updateBestPrice(variantId);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /* ---- Full option cards (radio-button expandable) ---- */
  _initFullOptionCards() {
    const cards = this.querySelectorAll('[data-full-card]');
    cards.forEach(card => {
      card.addEventListener('click', () => this._selectFullCard(card));
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._selectFullCard(card);
        }
      });
    });

    // Make sure the full form/atc reflect the SSR-active card on load.
    const active = this.querySelector('[data-full-card].section-mps__full-card--active');
    if (active) this._syncFullSelection(active);
  }

  _selectFullCard(card) {
    this.querySelectorAll('[data-full-card]').forEach(c => {
      c.classList.remove('section-mps__full-card--active');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('section-mps__full-card--active');
    card.setAttribute('aria-checked', 'true');
    this._syncFullSelection(card);
  }

  // Pulls variant id + price from the card and pushes them into the full
  // form + full ATC button. Intentionally does NOT touch the main product
  // price or the "Selected: …" label — those belong to the PDP product
  // (page identity), separate from the radio-card pick.
  _syncFullSelection(card) {
    if (!card) return;
    const variantId = card.dataset.variantId;
    const priceFormatted = card.dataset.priceFormatted || '';

    if (variantId) {
      const fullInput = this.querySelector('[data-full-variant-input]');
      if (fullInput) fullInput.value = variantId;
    }

    const priceEl = this.querySelector('[data-full-atc-price]');
    if (priceEl && priceFormatted) priceEl.textContent = priceFormatted;
  }

  /* ---- Review slider ---- */
  _initReviewSlider() {
    const track = this.querySelector('[data-reviews-track]');
    const prev = this.querySelector('[data-reviews-prev]');
    const next = this.querySelector('[data-reviews-next]');
    if (!track) return;

    let idx = 0;
    const count = track.children.length;

    const goTo = (i) => {
      idx = Math.max(0, Math.min(i, count - 1));
      track.style.transform = `translateX(-${idx * 100}%)`;
    };

    if (prev) prev.addEventListener('click', () => goTo(idx - 1));
    if (next) next.addEventListener('click', () => goTo(idx + 1));
  }

  /* ---- Routine add: only checked items, multi-add via /cart/add.js ----
     Mirrors what xProductCart.addToCart does on the Main ATC:
       1. POSTs items + sections=... so Shopify returns refreshed section HTML
       2. Replaces section innerHTML in place (cart count / drawer contents stay in sync)
       3. Resets xPopup state, then hands off cart UI to Shopflo via handleFloCartBtn
     We deliberately do NOT call Alpine.store('xMiniCart').openCart(). That helper
     calls handleFloCartBtn AND sets xPopup.open=true, which double-locks the body
     (Shopflo's wrap-script adds position:fixed; Alpine adds .overflow-hidden).
     The Shopflo lock can outlive our cart close → the page gets stuck. */
  _initRoutineAtcAll() {
    const btn = this.querySelector('[data-routine-atc-all]');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const items = Array.from(this.querySelectorAll('[data-routine-check]'))
        .filter(cb => cb.checked)
        .map(cb => ({ id: parseInt(cb.dataset.variantId, 10), quantity: 1 }))
        .filter(item => item.id);

      if (!items.length) {
        const original = btn.textContent;
        btn.textContent = 'Select at least one';
        setTimeout(() => { btn.textContent = original; }, 1500);
        return;
      }

      btn.disabled = true;
      btn.classList.add('is-loading');

      try {
        const sectionsToRender = (window.Alpine && Alpine.store && Alpine.store('xCartHelper'))
          ? Alpine.store('xCartHelper').getSectionsToRender()
          : [];
        const sectionsParam = sectionsToRender.map((s) => s.id).join(',');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items,
            sections: sectionsParam || undefined,
            sections_url: window.location.pathname,
          }),
        });
        const response = await res.json();

        /* Replace refreshed section HTML in place — same chain xProductCart uses. */
        if (response && response.sections && sectionsToRender.length) {
          sectionsToRender.forEach((section) => {
            const el = document.querySelector(section.selector);
            if (el && response.sections[section.id]) {
              el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
            }
          });
        }

        /* Update header cart bubble count if the snippet is present. */
        const bubble = document.querySelector('#cart-icon-bubble span');
        if (bubble && window.Alpine && Alpine.store && Alpine.store('xCartHelper')) {
          Alpine.store('xCartHelper').currentItemCount = parseInt(bubble.innerHTML, 10) || 0;
        }

        /* Reset popup state and hand cart UI off to Shopflo — no openCart(),
           so no double body-lock and no stuck page on close. */
        if (window.Alpine && Alpine.store && Alpine.store('xPopup')) {
          try { Alpine.store('xPopup').close(); } catch {}
        }
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        // if (typeof window.handleFloCartBtn === 'function') {
        //   window.handleFloCartBtn();
        // }
        Alpine.store('xPopup').close();
        Alpine.store('xMiniCart').openCart();
        Alpine.store('xCartHelper').currentItemCount = parseInt(document.querySelector('#cart-icon-bubble span').innerHTML);
        if (window.trackMetaAddToCart) {
          try { window.trackMetaAddToCart(response); } catch {}
        }
      } catch (err) {
        console.error('[MPS] Routine ATC error:', err);
      } finally {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
    });
  }

  /* ---- Delivery / pincode checker ---- */
  _initDeliveryChecker() {
    const input = this.querySelector('[data-pincode-input]');
    const btn = this.querySelector('[data-check-pincode]');
    const info = this.querySelector('[data-delivery-info]');
    if (!input || !btn || !info) return;

    const checkSvg = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M8.81697 13.5447L14.4195 7.94221L13.5413 7.06409L8.81697 11.7885L6.44197 9.41346L5.56384 10.2916L8.81697 13.5447ZM10.0011 17.9166C8.90613 17.9166 7.8769 17.7088 6.91342 17.2933C5.94995 16.8777 5.1119 16.3137 4.39926 15.6014C3.68662 14.889 3.12238 14.0513 2.70655 13.0883C2.29085 12.1252 2.08301 11.0962 2.08301 10.0014C2.08301 8.90638 2.29079 7.87714 2.70634 6.91367C3.1219 5.9502 3.68586 5.11214 4.39822 4.3995C5.11058 3.68686 5.94829 3.12263 6.91134 2.70679C7.8744 2.2911 8.90335 2.08325 9.99822 2.08325C11.0932 2.08325 12.1225 2.29103 13.0859 2.70658C14.0494 3.12214 14.8875 3.6861 15.6001 4.39846C16.3127 5.11082 16.877 5.94853 17.2928 6.91159C17.7085 7.87464 17.9163 8.9036 17.9163 9.99846C17.9163 11.0935 17.7086 12.1227 17.293 13.0862C16.8775 14.0496 16.3135 14.8877 15.6011 15.6003C14.8888 16.313 14.0511 16.8772 13.088 17.293C12.125 17.7087 11.096 17.9166 10.0011 17.9166ZM9.99967 16.6666C11.8608 16.6666 13.4372 16.0208 14.7288 14.7291C16.0205 13.4374 16.6663 11.861 16.6663 9.99992C16.6663 8.13881 16.0205 6.56242 14.7288 5.27075C13.4372 3.97909 11.8608 3.33325 9.99967 3.33325C8.13856 3.33325 6.56217 3.97909 5.27051 5.27075C3.97884 6.56242 3.33301 8.13881 3.33301 9.99992C3.33301 11.861 3.97884 13.4374 5.27051 14.7291C6.56217 16.0208 8.13856 16.6666 9.99967 16.6666Z" fill="#2A9939"/></svg>';

    const check = () => {
      const pin = input.value.trim();
      if (!/^\d{6}$/.test(pin)) {
        info.innerHTML = '<div class="section-mps__delivery-info-row"><span style="color:var(--mps-red)">Please enter a valid 6-digit PIN code.</span></div>';
        return;
      }
      const d = new Date();
      d.setDate(d.getDate() + 3);
      const dateStr = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
      info.innerHTML = `
        <div class="section-mps__delivery-info-row">${checkSvg}<span>Delivery by ${dateStr}</span></div>
        <div class="section-mps__delivery-info-row">${checkSvg}<span>COD Available</span></div>
      `;
    };

    btn.addEventListener('click', check);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') check(); });
  }

  /* ---- Helpers ---- */
  async _cartAdd(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Add to cart failed');
    }
    return res.json();
  }

  _openCartDrawer() {
    // Prefer the theme's Alpine store so all section refreshes / counters
    // stay in sync. Fall back to a plain custom event if it's not loaded yet.
    if (window.Alpine && Alpine.store && Alpine.store('xMiniCart')) {
      try { Alpine.store('xMiniCart').openCart(); } catch {}
      try { Alpine.store('xMiniCart').reLoad && Alpine.store('xMiniCart').reLoad(); } catch {}
    }
    document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
  }

  _formatMoney(cents) {
    if (cents == null) return '';
    const fmt = window.Shopify?.money_format || '&#8377;{{amount_no_decimals_with_comma_separator}}';
    const value = cents / 100;
    const intValue = Math.round(value);
    const decimal = value.toFixed(2);
    const intCommas = intValue.toLocaleString('en-IN');
    const decCommas = parseFloat(decimal).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const formatted = fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, intCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, decCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, intValue.toString())
      .replace(/\{\{\s*amount\s*\}\}/g, decimal);

    // Decode any HTML entities (e.g. &#8377; for ₹) so textContent renders cleanly.
    const decoder = document.createElement('textarea');
    decoder.innerHTML = formatted;
    return decoder.value;
  }

  /* ---- Buy it now ----
     Hands the currently-selected variant + quantity off to Shopflo's checkout,
     and restores the user's original cart when they come back.

     Same flow GoKwik uses on its own merchant pages (/cart.js → /cart/clear.js
     → /cart/add.js → checkout overlay). The cart is briefly modified, then
     restored automatically by the global script in layout/theme.liquid which
     re-fires on page load, bfcache restore (pageshow), and tab visibility.

     Hand-off uses Shopflo's globally-bound `handleFloCheckoutBtn()` (loaded
     by `bridge.shopflo.com/js/shopflo.bundle.js`). Falls back to native
     `/checkout` only if Shopflo isn't available.
  */
  _initBuyBtn() {
    const btn = this.querySelector('[data-buy-btn]');
    if (!btn) return;

    const resetBtn = () => {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      btn.dataset.busy = '0';
    };

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (btn.dataset.busy === '1') return;

      const cta = btn.closest('[data-main-cta]') || this;
      const variantInput = cta.querySelector('[data-variant-input]');
      const qtyInput = cta.querySelector('[data-quantity-input]');

      const variantId = variantInput && variantInput.value;
      const quantity = parseInt(qtyInput && qtyInput.value, 10) || 1;
      if (!variantId) return;

      // Active qty button may carry a discount code (e.g. qty=2 → 15% off).
      // Read at click time so it tracks the current selection.
      const activeQtyBtn = this.querySelector('[data-qty-btn].section-mps__qty-btn--active');
      const discountCode = activeQtyBtn && activeQtyBtn.dataset.discountCode;

      btn.dataset.busy = '1';
      btn.classList.add('is-loading');
      btn.disabled = true;

      try {
        // 1. Snapshot existing cart so we can restore it on return.
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await cartRes.json();
        const snapshot = (cart.items || []).map((it) => {
          const item = {
            id: it.variant_id,
            quantity: it.quantity,
          };
          if (it.properties && Object.keys(it.properties).length) {
            item.properties = it.properties;
          }
          const sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
          if (sp && sp.id) item.selling_plan = sp.id;
          return item;
        });
        try {
          if (snapshot.length > 0) {
            localStorage.setItem('_solved_buynow_snapshot', JSON.stringify(snapshot));
          } else {
            localStorage.removeItem('_solved_buynow_snapshot');
          }
        } catch (_) { /* private mode / quota — proceed without snapshot */ }

        // 2. Empty the cart.
        await fetch('/cart/clear.js', {
          method: 'POST',
          credentials: 'same-origin',
        });

        // 3. Add only the buy-now item.
        await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: quantity }),
        });

        // 3b. Apply the qty-tier discount code (if any). Shopify's
        //     /discount/{code} endpoint sets the discount-code cookie tied
        //     to the cart, which Shopflo (and native checkout) honor.
        if (discountCode) {
          try {
            await fetch('/discount/' + encodeURIComponent(discountCode), {
              credentials: 'same-origin',
              redirect: 'follow',
            });
          } catch (err) {
            console.warn('[MPS] Discount code apply failed (continuing):', err);
          }
        }

        // 4. Hand off to Shopflo via its globally-bound function. This is
        //    the same path the cart-page Checkout button uses, so the cart
        //    + session sync already works. Whether Shopflo renders this as
        //    a same-page modal or a full-page checkout is controlled by
        //    Shopflo's own dashboard config — not something the theme can
        //    override per call.
        if (typeof window.handleFloCheckoutBtn === 'function') {
          window.handleFloCheckoutBtn();
        } else {
          console.warn('[MPS] Shopflo handleFloCheckoutBtn unavailable; falling back to /checkout');
          window.location.href = '/checkout';
        }

        /* Safety-net reset: Shopflo can open as an in-tab overlay (no real
           page navigation), in which case neither `pageshow` nor
           `visibilitychange` ever fires when the user closes the overlay.
           Without this the button would stay stuck in is-loading. If a real
           navigation happens, the page is unloading and this timeout is
           thrown away — harmless. */
        setTimeout(resetBtn, 1500);
      } catch (err) {
        console.warn('[MPS] Buy Now failed:', err);
        resetBtn();
      }
    });

    /* Always reset on pageshow — covers bfcache restore when the user clicks
       browser back from a real Shopflo navigation. Initial page load is a
       no-op because the button starts non-loading. */
    window.addEventListener('pageshow', resetBtn);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') resetBtn();
    });
  }
}

if (!customElements.get('main-product-section')) {
  customElements.define('main-product-section', MainProductSection);
}

class MpsImageLightbox extends HTMLElement {
  connectedCallback() {
    this._buildDots();
    this._attachTrackScroll();
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKeyDown);
    this.addEventListener('click', this._onSelfClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKeyDown);
    document.documentElement.classList.remove('mps-lightbox-open');
  }

  _buildDots() {
    const dotsEl = this.querySelector('[data-lightbox-dots]');
    if (!dotsEl) return;
    const count = this.querySelectorAll('.section-mps__lightbox-slide').length;
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'section-mps__lightbox-dot' + (i === 0 ? ' is-active' : '');
      dotsEl.appendChild(dot);
    }
  }

  _attachTrackScroll() {
    const track = this.querySelector('[data-lightbox-track]');
    if (!track) return;
    track.addEventListener('scroll', () => {
      const idx = Math.round(track.scrollLeft / track.offsetWidth);
      this._updateDots(idx);
    }, { passive: true });
  }

  _updateDots(index) {
    this.querySelectorAll('.section-mps__lightbox-dot').forEach((d, i) => {
      d.classList.toggle('is-active', i === index);
    });
  }

  open(index) {
    this.classList.add('is-open');
    document.documentElement.classList.add('mps-lightbox-open');
    const track = this.querySelector('[data-lightbox-track]');
    if (track) {
      track.scrollTo({ left: track.offsetWidth * index, behavior: 'instant' });
    }
    this._updateDots(index);
    this.querySelector('[data-lightbox-close]')?.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('mps-lightbox-open');
  }

  _scrollCarouselTo(index) {
    const mobileSlider = document.querySelector('[data-mobile-slider]');
    if (!mobileSlider) return;
    const target = mobileSlider.querySelector(`[data-slide-index="${index}"]`);
    if (target) {
      mobileSlider.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
  }

  _onDocClick = (e) => {
    const btn = e.target.closest('[data-expand-btn]');
    if (btn) {
      const slide = btn.closest('[data-slide-index]');
      if (slide) {
        e.preventDefault();
        this.open(parseInt(slide.dataset.slideIndex, 10));
        return;
      }
    }
    const slideEl = e.target.closest('[data-slide-index]');
    // Video slides keep their inline player controls — don't hijack the tap to open the lightbox.
    if (slideEl && !slideEl.classList.contains('section-mps__slide--video')) {
      e.preventDefault();
      this.open(parseInt(slideEl.dataset.slideIndex, 10));
    }
  };

  _onSelfClick = (e) => {
    if (e.target.closest('[data-lightbox-close]')) {
      this.close();
      return;
    }
    const lightboxSlide = e.target.closest('.section-mps__lightbox-slide');
    // Don't close/zoom-out when interacting with a video slide's player controls.
    if (lightboxSlide && !lightboxSlide.classList.contains('section-mps__lightbox-slide--video')) {
      const track = this.querySelector('[data-lightbox-track]');
      const idx = track ? Math.round(track.scrollLeft / track.offsetWidth) : 0;
      this.close();
      this._scrollCarouselTo(idx);
    }
  };

  _onKeyDown = (e) => {
    if (e.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  };
}

if (!customElements.get('mps-image-lightbox')) {
  customElements.define('mps-image-lightbox', MpsImageLightbox);
}
